import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Check, Loader2, Shield, Clock, Sparkles, Zap, CreditCard, CalendarClock, ExternalLink, AlertTriangle, Crown, Building2, Users, UserPlus, BarChart3 } from 'lucide-react';

export default function Subscription({ user, token, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchStatus();
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubStatus(res.data);
    } catch {
      toast.error('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    setCheckingPayment(true);
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment status check timed out. Please refresh.');
      return;
    }
    try {
      const res = await axios.get(`${API}/subscription/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('Subscription activated! Welcome to Plexura AI Scribe.');
        fetchStatus();
        // Clear URL params
        window.history.replaceState({}, '', '/subscription');
        return;
      }
      if (res.data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    } catch {
      setCheckingPayment(false);
      toast.error('Error checking payment.');
    }
  };

  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.url) {
        setCheckoutUrl(res.data.url);
        // Small delay to let React render the link, then redirect
        setTimeout(() => {
          window.location.href = res.data.url;
        }, 100);
      } else {
        toast.error('No checkout URL received');
        setSubscribing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setSubscribing(false);
    }
  };

  const handleVerifyPayment = async () => {
    setVerifyingPayment(true);
    try {
      const res = await axios.post(`${API}/subscription/verify-payment`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.activated) {
        toast.success('Payment verified — subscription activated. Welcome!');
        await fetchStatus();
      } else {
        toast.error(
          res.data.checked_sessions
            ? "We couldn't find a completed payment yet. If you just paid, give Stripe ~30s and try again."
            : "No recent checkout attempts found on this account."
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed — please contact support.');
    } finally {
      setVerifyingPayment(false);
    }
  };

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await axios.post(`${API}/subscription/portal`, {
        return_url: window.location.origin + '/subscription',
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open billing portal');
      setOpeningPortal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  const isActive = subStatus?.active;
  const isTrial = subStatus?.status === 'trial';
  const isExpired = subStatus?.status === 'expired';
  const isSubscribed = subStatus?.status === 'subscribed';
  const plan = subStatus?.plan; // 'owner' | 'enterprise' | 'monthly' | undefined
  const isOwner = plan === 'owner';
  const isEnterprise = plan === 'enterprise' || subStatus?.is_enterprise === true;
  const isProfessional = isSubscribed && plan === 'monthly';

  // Plan presentation: label + description + icon + color tokens
  let planCard;
  if (isOwner) {
    planCard = {
      label: 'Owner Access',
      desc: 'Full platform access — complimentary owner account',
      Icon: Crown,
      bg: 'bg-indigo-50',
      border: 'border-indigo-200',
      iconColor: 'text-indigo-600',
    };
  } else if (isEnterprise && isSubscribed) {
    planCard = {
      label: 'Enterprise Plan',
      desc: 'Custom pricing — team management, SSO, BAA, priority SLA',
      Icon: Building2,
      bg: 'bg-indigo-50',
      border: 'border-indigo-200',
      iconColor: 'text-indigo-600',
    };
  } else if (isProfessional) {
    planCard = {
      label: 'Professional Plan',
      desc: '$79.99/month — full access to all clinician features',
      Icon: Shield,
      bg: 'bg-green-50',
      border: 'border-green-200',
      iconColor: 'text-green-600',
    };
  } else if (isTrial) {
    planCard = {
      label: `Free Trial — ${subStatus.days_left} day${subStatus.days_left !== 1 ? 's' : ''} left`,
      desc: 'Enjoy full access during your 7-day trial',
      Icon: Clock,
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      iconColor: 'text-purple-600',
    };
  } else {
    planCard = {
      label: 'Trial Expired',
      desc: 'Subscribe to continue using Plexura AI Scribe',
      Icon: Clock,
      bg: 'bg-red-50',
      border: 'border-red-200',
      iconColor: 'text-red-600',
    };
  }
  const PlanIcon = planCard.Icon;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #f8f4ff 50%, #e8eaf6 100%)' }}>
      <header className="bg-white/80 backdrop-blur-xl border-b border-white/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Button onClick={() => navigate('/')} variant="outline" className="rounded-xl shrink-0 px-3 sm:px-4">
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-purple-600 to-slate-700 bg-clip-text text-transparent truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Subscription
          </h1>
          <div className="w-10 sm:w-auto" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        {checkingPayment && (
          <Card className="p-6 mb-8 bg-blue-50 border-blue-200 rounded-2xl" data-testid="checking-payment">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <p className="text-sm font-medium text-blue-800">Verifying payment...</p>
            </div>
          </Card>
        )}

        {/* Current Status */}
        <Card className={`p-6 mb-8 rounded-2xl ${planCard.bg} ${planCard.border}`} data-testid="subscription-status">
          <div className="flex items-start gap-3">
            <PlanIcon className={`w-6 h-6 shrink-0 mt-0.5 ${planCard.iconColor}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-gray-900" data-testid="plan-label">
                  {planCard.label}
                </p>
                {(isOwner || isEnterprise) && (
                  <span
                    className="inline-flex items-center gap-1 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                    data-testid="plan-tier-badge"
                  >
                    <Sparkles className="w-3 h-3" />
                    {isOwner ? 'Owner' : 'Enterprise'}
                  </span>
                )}
                {isProfessional && (
                  <span
                    className="inline-flex items-center gap-1 bg-green-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                    data-testid="plan-tier-badge"
                  >
                    Professional
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-0.5" data-testid="plan-description">{planCard.desc}</p>
            </div>
          </div>
        </Card>

        {/* Team management CTA — Enterprise + whitelisted-owner entitlement */}
        {isEnterprise && (
          <Card
            className="p-5 sm:p-6 mb-8 rounded-2xl border border-indigo-200 shadow-sm"
            style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)' }}
            data-testid="team-management-card"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Team management
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                    Invite new clinicians, review pending invitations, or revoke access to your practice.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/practice')}
                data-testid="manage-seats-button"
                className="h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 shrink-0 w-full sm:w-auto"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Manage seats
              </Button>
            </div>
            <div className="mt-4 pt-4 border-t border-indigo-200 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-2 text-xs text-gray-600 min-w-0">
                <BarChart3 className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                <span>View practice-wide usage trends, consent coverage, audit trail, and download branded compliance reports (CSV / PDF).</span>
              </div>
              <Button
                onClick={() => navigate('/analytics')}
                data-testid="view-analytics-button"
                variant="outline"
                className="h-9 rounded-xl border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 font-semibold shrink-0"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics & reports
              </Button>
            </div>
          </Card>
        )}

        {/* Billing Summary — shown only for paid subscribers (not whitelisted owners) */}
        {isSubscribed && subStatus?.stripe_customer_id && (
          <Card className="p-5 sm:p-6 mb-8 bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-lg" data-testid="billing-summary">
            <div className="flex items-start justify-between gap-3 mb-5">
              <div>
                <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Billing Summary</h3>
                <p className="text-xs text-gray-500 mt-0.5">Manage your plan, payment method, and invoices</p>
              </div>
              {subStatus.cancel_at_period_end && (
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap" data-testid="billing-canceling-badge">
                  <AlertTriangle className="w-3 h-3" /> Canceling
                </span>
              )}
              {subStatus.stripe_status === 'past_due' && (
                <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap" data-testid="billing-past-due-badge">
                  <AlertTriangle className="w-3 h-3" /> Payment failed
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              {/* Card on file */}
              <div className="bg-gray-50 rounded-xl p-4" data-testid="billing-card-info">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  <CreditCard className="w-3.5 h-3.5" /> Payment Method
                </div>
                {subStatus.card_brand && subStatus.card_last4 ? (
                  <p className="text-sm font-medium text-gray-900">
                    {subStatus.card_brand.charAt(0).toUpperCase() + subStatus.card_brand.slice(1)} ···· {subStatus.card_last4}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">Card on file</p>
                )}
              </div>

              {/* Next billing */}
              <div className="bg-gray-50 rounded-xl p-4" data-testid="billing-next-date">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  <CalendarClock className="w-3.5 h-3.5" />
                  {subStatus.cancel_at_period_end ? 'Access Until' : 'Next Billing'}
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {subStatus.current_period_end
                    ? new Date(subStatus.current_period_end).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
                    : '—'}
                </p>
                {subStatus.cancel_at_period_end && (
                  <p className="text-xs text-amber-700 mt-1">Won't renew — you can reactivate from the billing portal any time.</p>
                )}
              </div>
            </div>

            <Button
              onClick={handleOpenPortal}
              disabled={openingPortal}
              data-testid="manage-billing-button"
              className="w-full h-11 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-semibold"
            >
              {openingPortal ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Opening portal…</>
              ) : (
                <><ExternalLink className="w-4 h-4 mr-2" />Manage Billing · Update Card · Cancel</>
              )}
            </Button>
            <p className="text-xs text-center text-gray-500 mt-2.5">
              Hosted securely by Stripe — we never see your card details.
            </p>
          </Card>
        )}
        {!isSubscribed && (
          <Card className="p-5 sm:p-8 bg-white/90 backdrop-blur-xl rounded-3xl border border-purple-200 shadow-2xl" data-testid="pricing-card">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold mb-4">
                <Sparkles className="w-3 h-3" />
                {isTrial ? 'Upgrade Now' : 'Subscribe'}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Plexura AI Scribe</h2>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl sm:text-5xl font-bold text-gray-900">$79</span>
                <span className="text-xl sm:text-2xl text-gray-500">.99</span>
                <span className="text-gray-500 ml-1">/month</span>
              </div>
              {isTrial && <p className="text-sm text-purple-600 mt-2">Subscribe before your trial ends to keep access</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {[
                'Real-time session transcription',
                'AI speaker diarization',
                'Comprehensive DSM-5 diagnosis',
                'SOAP medical notes (auto-generated)',
                'Psychotherapy progress notes',
                'ICD-10, CPT, E/M billing codes',
                '8 session templates',
                'Push to any EHR system',
                'FHIR & CCD exports',
                'Universal browser extension (Chrome, Edge & Safari)',
                'Session timing (EST)',
                'HIPAA compliant'
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 shrink-0" />
                  {feature}
                </div>
              ))}
            </div>

            <Button
              onClick={handleSubscribe}
              disabled={subscribing}
              data-testid="subscribe-button"
              className="w-full h-14 rounded-2xl bg-gradient-to-r from-purple-600 to-slate-600 hover:from-purple-700 hover:to-slate-700 text-white text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
            >
              {subscribing ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Redirecting to checkout...</>
              ) : (
                <><Zap className="w-5 h-5 mr-2" />Subscribe — $79.99/month</>
              )}
            </Button>
            {checkoutUrl && (
              <a
                href={checkoutUrl}
                data-testid="checkout-link"
                className="block text-center text-sm text-purple-600 hover:text-purple-800 underline mt-3"
              >
                Click here if not redirected automatically
              </a>
            )}

            {/* Self-service rescue: paid but stuck here? */}
            <div className="mt-6 pt-5 border-t border-purple-100 text-center">
              <p className="text-xs text-gray-500 mb-2">
                Already paid but still seeing this screen?
              </p>
              <Button
                onClick={handleVerifyPayment}
                disabled={verifyingPayment}
                variant="outline"
                data-testid="verify-payment-button"
                className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-50 hover:text-purple-800 font-semibold"
              >
                {verifyingPayment ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying with Stripe…</>
                ) : (
                  <><Shield className="w-4 h-4 mr-2" />Verify my payment</>
                )}
              </Button>
            </div>
          </Card>
        )}

        {isSubscribed && (
          <Card className="p-8 bg-white/90 backdrop-blur-xl rounded-3xl border border-green-200" data-testid="active-subscription-card">
            <div className="text-center">
              {isOwner || isEnterprise ? (
                <>
                  {isOwner
                    ? <Crown className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
                    : <Building2 className="w-12 h-12 text-indigo-500 mx-auto mb-4" />}
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {isOwner ? 'Owner access enabled' : 'Enterprise plan active'}
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {isOwner
                      ? "You have complimentary owner access to every feature — no billing required."
                      : "Your Enterprise plan includes team management, SSO, BAA, and priority support. Reach out to your account owner for billing questions."}
                  </p>
                </>
              ) : (
                <>
                  <Shield className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h2>
                  <p className="text-gray-600 mb-6">Your Professional subscription is active. Enjoy unlimited access to all features.</p>
                </>
              )}
              <Button onClick={() => navigate('/')} className="rounded-xl bg-green-600 hover:bg-green-700">
                Go to Dashboard
              </Button>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
